<template>
  <div>
    <header class="section-header">
      <div class="section-header__text">
        <h1 class="section-header__title">
          {{ $t('unit.public.profile.subunits.header') }}
        </h1>
        <p>{{ $t('unit.public.profile.subunits.description') }}</p>
      </div>
    </header>
    <SearchPaginatedList
      :is-initial-loading="isInitialLoading"
      :is-searching="isSearching"
      :query="searchQuery"
      :page-data="units"
    >
      <template #search-bar="{buttonAttrs, inputAttrs}">
        <div class="f-field-search">
          <button
            class="f-field-search__submit"
            :aria-label="$t('search')"
            v-bind="buttonAttrs"
          />
          <input
            v-model.trim="searchQuery"
            :placeholder="$t('search.units')"
            type="search"
            class="f-field-search__input f-field-search__input--card"
            v-bind="inputAttrs"
          >
        </div>
      </template>
      <template #list-skeleton>
        <UnitSkeleton
          :count="4"
          class="card-list"
        />
      </template>
      <template #list>
        <ul class="card card-list">
          <li
            v-for="unit in units.items"
            :key="unit.id"
            class="card-row"
          >
            <Avatar
              :name="unit.name"
              class="card-row__figure figure--letter"
            />

            <div class="card-row__text">
              <UnitBreadcrumb :unit="unit" />
              <h1 class="card-row__title h5--ssp">
                <RouterLink
                  :to="{ name: 'UnitPage', params: { unitID: unit.id } }"
                  class="card-row__link"
                >
                  {{ unit.name }}
                </RouterLink>
              </h1>
            </div>
          </li>
        </ul>
      </template>
      <template #pagination>
        <PaginationBar
          v-if="units"
          :current-page="currentPage"
          :total-items="units.totalItems"
          :items-per-page="perPage"
        />
      </template>
      <template #empty-state>
        <UnitEmptyState>
          <template #title>
            {{ $t('unit.public.profile.subunits.empty.state.search.results.header', { query: emptyStateSearchQuery }) }}
          </template>
          <template #description>
            {{ $t('unit.public.profile.subunits.empty.state.search.results.description') }}
          </template>
        </UnitEmptyState>
      </template>
    </SearchPaginatedList>
  </div>
</template>
<script>
import Avatar from '#src/components/utils/Avatar.vue'
import UnitBreadcrumb from '#src/components/units/UnitBreadcrumb.vue'
import PaginationBar from '#src/components/utils/PaginationBar.vue'
import useUnitsAPI from '#src/api/units'
import UnitEmptyState from '#src/components/empty-states/Unit.vue'
import UnitSkeleton from '#src/components/loading-skeletons/Unit.vue'
import SearchPaginatedList from '#src/components/utils/SearchPaginatedList.vue'
import { useI18n } from 'vue-i18n'
import { useHeadSafe } from '@unhead/vue'

export default {
  components: {
    Avatar,
    UnitBreadcrumb,
    PaginationBar,
    UnitEmptyState,
    UnitSkeleton,
    SearchPaginatedList
  },
  inject: ['$axios'],
  beforeRouteUpdate(to, from, next) {
    if (this.searchTimeout) clearTimeout(this.searchTimeout)
    this.isSearching = true
    this.searchTimeout = setTimeout(async () => {
      this.emptyStateSearchQuery = to.query.q

      this.currentPage = Number(to.query.page) || 1
      this.units = await this.UnitsAPI.getSubUnits({ unitID: this.unitID, page: this.currentPage, perPage: this.perPage, sort: '', q: to.query.q })
      this.isSearching = false
    }, 500)
    if (!import.meta.env.SSR && to.query.perPage) localStorage.setItem('perPage', Number(to.query.perPage) || 10)
    next()
  },
  props: {
    unitID: {
      type: String,
      required: true
    },
    page: {
      type: Number,
      required: true
    },
    q: {
      type: String,
      default: ''
    },
    perPage: {
      type: Number,
      default: import.meta.env.SSR ? 10 : Number(localStorage.getItem('perPage')) || 10
    }
  },
  setup() {
    const { t } = useI18n({
      fallbackWarn: false,
      silentTranslationWarn: true,
      silentFallbackWarn: true,
      messages: {
        pt: {
          title: 'Subunidades',
          description: 'Uma lista com todos as subunidades'
        },
        en: {
          title: 'Subunits',
          description: 'A list with all the subunits'
        }
      }
    })
    const metaInfo = {
      title: t('title'),
      meta: [{ name: 'description', content: t('description') }]
    }
    useHeadSafe(metaInfo)
    return { t }
  },
  data() {
    return {
      UnitsAPI: useUnitsAPI(this.$axios),
      isInitialLoading: true,
      isSearching: false,
      currentPage: this.page,
      units: undefined,
      searchQuery: this.q,
      searchTimeout: undefined
    }
  },
  watch: {
    async searchQuery(newValue, oldValue) {
      if (newValue === oldValue) return

      const query = { ...this.$route.query, q: newValue }
      if (newValue === '') {
        delete query.q
      }
      delete query.page
      await this.$router.replace({ query })
    }
  },
  beforeUnmount() {
    clearTimeout(this.searchTimeout)
  },
  async mounted() {
    this.units = await this.UnitsAPI.getSubUnits({ unitID: this.unitID, page: this.currentPage, perPage: this.perPage, sort: '', q: this.q })
    this.isInitialLoading = false
    // purposefully makes search query non-reactive to display on empty state
    this.emptyStateSearchQuery = this.searchQuery
  }
}
</script>
